import { useMemo } from 'react'
import { Calendar, CheckCircle2 } from 'lucide-react'
import type { CycleDocument } from '@/lib/db/rxdb/hooks/entities/cycles'
import { Badge } from '@/components/ui/badge'
import { useCycleWorkItemsByCycle } from '@/lib/db/rxdb/hooks/entities/cycleWorkItems'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'

interface CycleCardProps {
  cycle: CycleDocument
  projectId: string
  isActive?: boolean
  onClick?: () => void
}

type CycleBadge = { label: string; variant: 'default' | 'secondary' | 'outline' }

// Bound separately so it can serve as the fallback: every read from a
// `Record<string, T>` is `T | undefined` under noUncheckedIndexedAccess.
const DRAFT_BADGE: CycleBadge = { label: 'Draft', variant: 'secondary' }

const STATUS_BADGE: Record<string, CycleBadge> = {
  active: { label: 'Active', variant: 'default' },
  draft: DRAFT_BADGE,
  completed: { label: 'Completed', variant: 'outline' },
}

export function CycleCard({ cycle, isActive, onClick }: CycleCardProps) {
  const { date: formatDate } = useDateTimeFormatters()
  const { cycleWorkItems } = useCycleWorkItemsByCycle(cycle.id)
  const taskCount = cycleWorkItems?.length ?? 0
  const completedCount = useMemo(
    () => cycleWorkItems?.filter((cwi) => cwi.progressTypeAtClose === 'CD' || cwi.progressTypeAtClose === 'CL').length ?? 0,
    [cycleWorkItems],
  )
  const progress = taskCount > 0 ? Math.round((completedCount / taskCount) * 100) : 0

  const badge = STATUS_BADGE[cycle.status] ?? DRAFT_BADGE

  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-xl border p-4 transition-colors hover:border-input ${
        isActive ? 'border-brand/50 bg-brand-tint/30' : 'bg-card'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{cycle.name}</span>
          <Badge variant={badge.variant} className="text-[10px] px-1.5 py-0">
            {badge.label}
          </Badge>
        </div>
        {taskCount > 0 && (
          <span className="text-xs text-muted-foreground">
            {completedCount}/{taskCount} tasks
          </span>
        )}
      </div>
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Calendar className="size-3" />
          {formatDate(new Date(cycle.startDate), { month: 'short', day: 'numeric' })} &mdash; {formatDate(new Date(cycle.endDate), { month: 'short', day: 'numeric' })}
        </span>
        {cycle.status === 'completed' && (
          <span className="flex items-center gap-1 text-muted-foreground">
            <CheckCircle2 className="size-3" />
            Completed
          </span>
        )}
      </div>
      {taskCount > 0 && cycle.status !== 'completed' && (
        <div className="mt-3 h-1.5 w-full rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full bg-foreground/70 transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </button>
  )
}
